import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { StoryLoanCard } from 'app/screens/shared/components/story-loan-card';
import { useNavigation } from 'app/hooks/navigation';
import { useLoansWithoutPosts } from 'app/network/post';
import { NewPostContent } from 'app/screens/shared/new-post';
import type { Loan } from 'app/types/loan';
import { styles } from '../styles';

export function OfferHubStoriesTab({
  active,
  visible,
  backInHeader = false,
}: {
  active: boolean;
  visible: boolean;
  backInHeader?: boolean;
}) {
  const { t } = useTranslation('offer-hub');
  const navigation = useNavigation<'new-post'>();
  const pagePadding = usePagePadding();
  const loans = useLoansWithoutPosts(active);
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const back = () => setSelectedLoan(null);

  return (
    <View
      style={[styles.flex, !visible && { display: 'none' }]}
      accessibilityElementsHidden={!visible}
      importantForAccessibility={visible ? 'auto' : 'no-hide-descendants'}
    >
      {selectedLoan ? (
        <NewPostContent
          key={selectedLoan.id}
          embedded
          backInHeader={backInHeader}
          navigation={navigation}
          route={{
            key: `story:${selectedLoan.id}`,
            name: 'new-post',
            params: { loanId: selectedLoan.id, listingId: selectedLoan.listingId },
          }}
          onBack={back}
          onPosted={back}
        />
      ) : (
        <Page paddingStyle={{ ...pagePadding, paddingTop: 16 }}>
          <Column gap="spacing.general.x5">
            <Column gap="spacing.general.x2" paddingBottom="spacing.general.x2">
              <Text fontSize={28} lineHeight={34} fontWeight="700">
                {t('storyPickTitle')}
              </Text>
              <Text color="text.secondary" fontSize={14} lineHeight={21}>
                {t('storyPickHint')}
              </Text>
            </Column>
            {loans.isError ? (
              <>
                <Text color="text.error">{t('storyLoadingError')}</Text>
                <Button testID="story-retry" variant="secondary" onPress={() => void loans.refetch()}>
                  {t('retry')}
                </Button>
              </>
            ) : loans.data.length ? (
              <Column gap="spacing.general.x3">
                <Text fontSize={13} fontWeight="600" color="text.secondary">
                  {t('storyMemories')}
                </Text>
                {loans.data.map((loan) => (
                  <StoryLoanCard
                    key={loan.id}
                    loan={loan}
                    testID={`story-loan-${loan.id}`}
                    onPress={() => setSelectedLoan(loan)}
                  />
                ))}
              </Column>
            ) : (
              <Text color="text.secondary">{t(loans.isPending ? 'storyLoading' : 'storyEmpty')}</Text>
            )}
          </Column>
        </Page>
      )}
    </View>
  );
}
